"use client";

import { Ref, useRef, useState } from "react";

import Badge from "@/components/common/Badge/Badge";
import { useIsMobile } from "@/hooks/useIsMobile";
//import { useVariant } from "@/hooks/useVariant";

import styles from "./HomepageHero.module.scss";
import { HomepageHeroLifestage, HomepageHeroProps } from "./HomepageHero.types";
import HomepageHeroBenefits from "./HomepageHeroBenefits";
import HomepageHeroDesktop from "./HomepageHeroDesktop";
import HomepageHeroImages from "./HomepageHeroImages";
import HomepageHeroMobile from "./HomepageHeroMobile";
import HomepageHeroTitle from "./HomepageHeroTitle";

const HomepageHero = ({ data, promoCf, isNarrow = true }: HomepageHeroProps) => {
  const { buttons, benefits, heading, images } = data;
  const [core, setCore] = useState<HomepageHeroLifestage>("balance");
  const [newCore, setNewCore] = useState<HomepageHeroLifestage>("balance");
  const [fadeState, setFadeState] = useState<"fadeIn" | "fadeOut">("fadeIn");
  const [fadeNewState, setNewFadeState] = useState<"fadeIn" | "fadeOut">("fadeIn");
  const timers = useRef<number[]>([]);
  const promo = promoCf?.savingsData?.web?.hp?.women;
  const title = promo?.title?.text;
  const subtitle = promo?.subtitle?.text;
  /*
   * const variant = useVariant("vwo31900", "control", false);
   * const isVwoActive = variant === "a" && Boolean(promoCf);
   */
  const isVwoActive = false;

  const setCoreFade = (next: HomepageHeroLifestage) => {
    if (next === core) return;
    timers.current.forEach((id) => window.clearTimeout(id));
    setFadeState("fadeOut");
    const t1 = window.setTimeout(() => {
      setNewCore(next);
      setNewFadeState("fadeIn");
      const t2 = window.setTimeout(() => {
        setCore(next);
        setFadeState("fadeIn");
      }, 150);
      timers.current = [t2];
    }, 150);
    timers.current = [t1];
  };

  const getFadeState = (key: HomepageHeroLifestage) =>
    core === key ? fadeState : newCore === key ? fadeNewState : null;

  const titleSection = (
    <HomepageHeroTitle
      heading={typeof title === "string" ? title : heading}
      subtitle={typeof subtitle === "string" ? subtitle : undefined}
      women={buttons.womenPcp}
      men={buttons.men}
      quiz={buttons.quiz}
      isVwoActive={isVwoActive}
    />
  );

  const benefitsSection = (
    <HomepageHeroBenefits items={benefits[newCore].items} shop={buttons[newCore]} />
  );

  const imagesSection = (imgRef: Ref<HTMLDivElement>) => (
    <HomepageHeroImages images={images} imgRef={imgRef} getFadeState={getFadeState} />
  );

  const layout = { data, newCore, setCoreFade, titleSection, benefitsSection, imagesSection };
  /* Mobile-first: SSR/first paint uses the mobile layout; desktop switches after matchMedia. */
  const isMobile = useIsMobile(1280, isNarrow);

  return (
    <div className={styles.wrapper}>
      <div className="relative w-full max-w-[1600px] mx-auto px-4 md:px-10 min-[1470px]:px-0">
        <div className="badge-container text-center mt-4 mb-1.5 md:mb-3.5 xl:mb-0">
          <Badge
            classes="mx-auto flex flex-col text-center"
            mode="dark"
            lineOneProps={{ className: "Body-20-Dia-Strong mb-[2px] text-nova-base-white" }}
            lineTwoProps={{ className: "Body-20-Dia-Normal text-nova-transparency-white-60" }}
          />
        </div>
        {isMobile ? <HomepageHeroMobile {...layout} /> : <HomepageHeroDesktop {...layout} />}
      </div>
    </div>
  );
};

export default HomepageHero;
